/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { Auth } from "aws-amplify";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { APIGateways } from "../../../utility/APIGateways";
import { SENTIMENT_AVERAGE_VALUES, SENTIMENT_TYPE } from "../../../utility/constants";
import { msToTime } from "../../../utility";
const COMPONENT_NAME = "adminCCPServices/RealtimeSentimentAnalysis";

const _generateMessageText = (message) => {
	const messageSpans = [];
	if (message.IssuesDetected) {
		message.IssuesDetected.forEach((issue, index) => {
			if (index === 0) {
				messageSpans.push(
					<span key={`issue ${index} start`}>
						{message.Content.substring(0, issue.CharacterOffsets.BeginOffsetChar)}
					</span>,
				);
			}
			messageSpans.push(
				<span key={`issue ${index} issue text`} style={{ fontWeight: "bolder" }} title={"Potential issue detected"}>
					{message.Content.substring(issue.CharacterOffsets.BeginOffsetChar, issue.CharacterOffsets.EndOffsetChar)}
				</span>,
			);
			if (index === message.IssuesDetected.length - 1) {
				messageSpans.push(
					<span key={`issue ${index} end`}>{message.Content.substring(issue.CharacterOffsets.EndOffsetChar + 1)}</span>,
				);
			}
		});
	} else {
		return <span>{message.Content}</span>;
	}
	return messageSpans;
};

const _formatResults = (segments) => {
	const formattedSentimentResults = { categories: { None: { length: 0 } }, messages: {} };
	let customerMessageCount = 0;
	segments.forEach((segment) => {
		if (segment.Transcript) {
			formattedSentimentResults.messages = {
				...formattedSentimentResults.messages,
				[segment.Transcript.BeginOffsetMillis]: {
					...segment.Transcript,
					category: "None",
					messageDisplayText: _generateMessageText(segment.Transcript),
					timeToDisplay: msToTime(segment.Transcript.BeginOffsetMillis),
				},
			};
			if (segment.Transcript.ParticipantRole === "CUSTOMER") {
				formattedSentimentResults.customerSentimentAverage =
					(formattedSentimentResults.customerSentimentAverage || 0) +
					SENTIMENT_TYPE[segment.Transcript.Sentiment].weightingValue;
				customerMessageCount++;
			}

			formattedSentimentResults.categories.None.length++;
		} else if (segment.Categories) {
			Object.keys(segment.Categories.MatchedDetails).forEach((match) => {
				segment.Categories.MatchedDetails[match].PointsOfInterest.forEach((POI) => {
					formattedSentimentResults.messages[POI.BeginOffsetMillis] = {
						...formattedSentimentResults.messages[POI.BeginOffsetMillis],
						category: match,
					};
					formattedSentimentResults.categories.None.length--;
				});
				formattedSentimentResults.categories[match] = [
					...(formattedSentimentResults.categories[match] || []),
					...segment.Categories.MatchedDetails[match].PointsOfInterest,
				];
			});
		}
	});
	if (formattedSentimentResults.customerSentimentAverage !== undefined) {
		const average = formattedSentimentResults.customerSentimentAverage / customerMessageCount;
		if (average <= SENTIMENT_AVERAGE_VALUES.NEGATIVE.value) {
			formattedSentimentResults.customerSentimentAverage = SENTIMENT_AVERAGE_VALUES.NEGATIVE;
		} else if (average >= SENTIMENT_AVERAGE_VALUES.POSITIVE.value) {
			formattedSentimentResults.customerSentimentAverage = SENTIMENT_AVERAGE_VALUES.POSITIVE;
		} else {
			formattedSentimentResults.customerSentimentAverage = SENTIMENT_AVERAGE_VALUES.NEUTRAL;
		}
	}
	return formattedSentimentResults;
};

/**
 * This method makes an api call to a lambda via an api gateway secured by cognito pool to
 * get the realtime sentiments analysis results from contact lens in amazon connect for a specific contactId.
 * @param {String} contactId The id for the contact to search for sentiment analysis results for in the lambda.
 */
export const getSentimentAnalysisResults = async (contactId) => {
	try {
		const token = (await Auth.currentSession()).getIdToken().getJwtToken();

		let sentimentResults = await axios.get(`${APIGateways.sentimentAnalysisEndpoint}/${contactId}`, {
			headers: {
				Authorization: token,
			},
		});
		if (!sentimentResults?.data?.errorMessage && sentimentResults?.data?.Segments) {
			const segments = sentimentResults?.data?.Segments;
			return _formatResults(segments);
		} else {
			return sentimentResults?.data;
		}
	} catch (error) {
		v2.error("Error while getting realtime sentiment analysis results", {
			additionalData: { error: errorToObject(error), data: { contactId } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.CONTACT_HISTORY_GET_API_ERROR,
		});

		throw error;
	}
};
